import { Dispatch, FC, SetStateAction } from 'react';
import {
  Alert,
  Divider,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import { CalendarEvent, InterviewBlock, UserEvent } from '@features/events';
import {
  borderGrey,
  white,
} from '@styles/colors';
import { bold, light, medium } from '@styles/fontWeights';
import ApplicantEventInfoModal from '../ApplicantEventInfoModal/ApplicantEventInfoModal';
import { Application } from '@features/applications';
import { getMonthName } from '@features/applications/components/BulkActions/utils';

type ApplicantUpcomingEventsProps = {
  events: CalendarEvent[];
  setSelectedEvent: Dispatch<SetStateAction<InterviewBlock | UserEvent | null>>;
  setSelectedBlock: Dispatch<SetStateAction<number | string | null>>;
  singleBlockInterview: InterviewBlock | UserEvent | null;
  setSingleBlockInterview: Dispatch<
    SetStateAction<InterviewBlock | UserEvent | null>
  >;
  handleChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void;
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  setSelectedApplication: Dispatch<SetStateAction<Application | null>>;
  selectedApplication: Application | null;
  dateInfo: {
    month: string;
    year: string;
  };
};

const ApplicantUpcomingEvents: FC<ApplicantUpcomingEventsProps> = ({
  events,
  setSelectedBlock,
  setSelectedEvent,
  setSelectedApplication,
  selectedApplication,
  singleBlockInterview,
  setSingleBlockInterview,
  dateInfo,
}) => {
  const now = new Date();

  const filterByMonthAndYear = (events: any[], month: number, year: number) => {
    const startOfMonthDate = dayjs
      .utc()
      .year(year)
      .month(month - 1)
      .startOf('month');
    const endOfMonthDate = dayjs
      .utc()
      .year(year)
      .month(month - 1)
      .endOf('month');

    return events.filter((event) => {
      const eventStart = dayjs.utc(event.start);
      return (
        eventStart.isSameOrAfter(startOfMonthDate) &&
        eventStart.isSameOrBefore(endOfMonthDate)
      );
    });
  };

  return (
    <Stack
      bgcolor={white}
      p={3}
      spacing={3}
      borderRadius={8}
      sx={{ width: '100%' }}
    >
      <Typography variant="h3" textTransform="uppercase" fontWeight="bold">
        Upcoming Events
      </Typography>
      <Divider />
      {filterByMonthAndYear(
        events,
        Number(dateInfo.month),
        Number(dateInfo.year),
      )?.filter((event) => new Date(event.start) >= now)?.length ? (
        <List
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {filterByMonthAndYear(
            events,
            Number(dateInfo.month),
            Number(dateInfo.year),
          )
            .filter((event) => new Date(event.start) >= now)
            .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
            .map((event) => (
              <ListItem key={event.event.id} sx={{ p: 0 }}>
                <ListItemButton
                  sx={{ borderBottom: `1px solid ${borderGrey}` }}
                  onClick={() => {
                    setSelectedEvent(event.event);
                    setSelectedBlock(event.event.id);
                  }}
                >
                  <Stack spacing={3} direction="row" alignItems="center">
                    <Stack spacing={0} alignItems="center">
                      <Typography
                        variant="h2"
                        component="p"
                        fontWeight={medium}
                      >
                        {dayjs(event.start).format('DD')}
                      </Typography>
                      <Typography variant="h4" component="p" fontWeight={light}>
                        {dayjs(event.start).format('MMM')}
                      </Typography>
                    </Stack>
                    <Stack spacing={0}>
                      <Typography
                        fontWeight={bold}
                      >{`${dayjs(event?.slotStart ?? event.start).format('hh:mm')} - ${dayjs(event?.slotEnd ?? event.end).format('hh:mm a')}`}</Typography>
                      <Typography>{event.title}</Typography>
                    </Stack>
                  </Stack>
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      ) : (
        <Alert severity="info">
          You have no upcoming blocks for{' '}
          <b>
            {' '}
            {getMonthName(dateInfo?.month)} {dateInfo.year}
          </b>
        </Alert>
      )}
      {singleBlockInterview && (
        <ApplicantEventInfoModal
          event={singleBlockInterview}
          handleClose={() => setSingleBlockInterview(null)}
          setSelectedApplication={setSelectedApplication}
          selectedApplication={selectedApplication}
        />
      )}
    </Stack>
  );
};

export default ApplicantUpcomingEvents;
